import React, { useEffect, useMemo, useRef, useState } from 'react';
import FinishSelector from './FinishSelector';
import MaterialSelectorModal from './MaterialSelectorModal';

const STANDARD_SIZES = [
  { id: '', label: 'Custom size', width: '', height: '' },
  { id: 'A0', label: 'A0 (841 × 1189 mm)', width: '841', height: '1189' },
  { id: 'A1', label: 'A1 (594 × 841 mm)', width: '594', height: '841' },
  { id: 'A2', label: 'A2 (420 × 594 mm)', width: '420', height: '594' },
  { id: 'A3', label: 'A3 (297 × 420 mm)', width: '297', height: '420' },
  { id: 'A4', label: 'A4 (210 × 297 mm)', width: '210', height: '297' },
  { id: 'A5', label: 'A5 (148 × 210 mm)', width: '148', height: '210' },
  { id: 'A6', label: 'A6 (105 × 148 mm)', width: '105', height: '148' },
  { id: 'A7', label: 'A7 (74 × 105 mm)', width: '74', height: '105' },
];

const WEEDING_OPTIONS = [
  { value: 'easy', label: 'Easy — simple shapes' },
  { value: 'medium', label: 'Medium — large lettering, moderately sized shapes' },
  { value: 'hard', label: 'Hard — intricate detail, small letters/fiddly shapes' },
];

const PieceCard = ({
  piece,
  pieceNumber,
  isExpanded,
  onToggle,
  onChange,
  onRemove,
  jobTypes = [],
  getMaterialsForJobType,
  getFinishesForJobType,
  allowRemove = true,
  maxQuantity = 9999,
  allowMultipleMaterials = true,
  finishingMaterials = {},
  isLocked = false,
}) => {
  const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [pendingName, setPendingName] = useState(piece.name || '');
  const renameInputRef = useRef(null);

  const updatePiece = (updates) => {
    onChange({ ...piece, ...updates });
  };

  const handleFieldChange = (field, value) => {
    updatePiece({ [field]: value });
  };

  const availableMaterials = useMemo(() => {
    if (typeof getMaterialsForJobType !== 'function' || !piece.jobType) {
      return [];
    }
    return getMaterialsForJobType(piece.jobType) || [];
  }, [getMaterialsForJobType, piece.jobType]);

  const availableFinishes = useMemo(() => {
    if (typeof getFinishesForJobType !== 'function' || !piece.jobType) {
      return [];
    }
    return getFinishesForJobType(piece.jobType, piece.materials || []) || [];
  }, [getFinishesForJobType, piece.jobType, piece.materials]);

  const showWeedingDropdown =
    piece.jobType === 'cutvinyl' || piece.jobType === 'vinyl-stickers';
  const isRollerBanner = piece.jobType === 'roller-banner';
  const weedingHelpText =
    piece.jobType === 'vinyl-stickers'
      ? 'Adjusts labour for sticker weeding — increase for intricate cut paths before application.'
      : 'Adjusts labour for detailed vinyl — pick a higher level for intricate lettering.';

  const jobTypeName =
    jobTypes.find((jobType) => jobType.id === piece.jobType)?.name ?? 'Not selected';

  const materialDisplay = useMemo(() => {
    if (!piece.materials?.length) {
      return '';
    }

    return piece.materials
      .map((selection) => {
        const match = availableMaterials.find(
          (material) => material.id === selection.id && material.type === selection.type,
        );
        return match?.name || selection.name || selection.id;
      })
      .join(', ');
  }, [availableMaterials, piece.materials]);

  const requiresRouterLength = useMemo(
    () =>
      Array.isArray(piece.finishes) &&
      piece.finishes.some((finishId) => finishId && finishId.toLowerCase().includes('router')),
    [piece.finishes],
  );

  const handleMaterialsConfirm = (selectedMaterials) => {
    const firstSelection = selectedMaterials[0];

    if (piece.jobType === 'roller-banner' && firstSelection) {
      const widthFromMaterial = firstSelection.width;
      const heightFromMaterial = firstSelection.height;

      updatePiece({
        materials: selectedMaterials,
        width: widthFromMaterial ?? piece.width,
        height: heightFromMaterial ?? piece.height,
        standardSize: '',
      });
    } else {
      updatePiece({ materials: selectedMaterials });
    }

    setIsMaterialModalOpen(false);
  };

  const handleStandardSizeChange = (event) => {
    const selection = event.target.value;
    if (!selection) {
      updatePiece({ standardSize: '' });
      return;
    }

    const size = STANDARD_SIZES.find((entry) => entry.id === selection);
    if (size) {
      updatePiece({
        standardSize: size.id,
        width: size.width,
        height: size.height,
      });
    }
  };

  const handleDimensionChange = (field, value) => {
    updatePiece({
      [field]: value,
      standardSize: '',
    });
  };

  const handleMaterialsCancel = () => {
    setIsMaterialModalOpen(false);
  };

  const toggleMaterialModal = (event) => {
    event.stopPropagation();
    if (isLocked) return;
    setIsMaterialModalOpen(true);
  };

  const handleRemove = (event) => {
    event.stopPropagation();
    onRemove();
  };

  const handleRenameStart = (event) => {
    event.stopPropagation();
    setPendingName(piece.name || '');
    setIsRenaming(true);
  };

  const handleRenameCancel = (event) => {
    if (event) event.stopPropagation();
    setIsRenaming(false);
    setPendingName(piece.name || '');
  };

  const handleRenameSave = (event) => {
    if (event) event.stopPropagation();
    const trimmed = pendingName.trim();
    updatePiece({ name: trimmed });
    setIsRenaming(false);
  };

  const handleRenameKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleRenameSave(event);
    } else if (event.key === 'Escape') {
      handleRenameCancel(event);
    }
  };

  // Auto-apply default finishes when job type changes
  // Store previous job type to detect changes
  const prevJobTypeRef = useRef(piece.jobType);

  useEffect(() => {
    const jobType = piece.jobType;
    const hasJobTypeChanged = prevJobTypeRef.current !== jobType;

    // console.log(`PieceCard Effect: jobType=${jobType}, prev=${prevJobTypeRef.current}, changed=${hasJobTypeChanged}`);

    if (!jobType || !hasJobTypeChanged) {
      prevJobTypeRef.current = jobType;
      return;
    }

    const selectedJobType = jobTypes.find((jt) => jt.id === jobType);
    if (!selectedJobType) {
      prevJobTypeRef.current = jobType;
      return;
    }

    const availableFinishIds = new Set(availableFinishes.map((f) => f.id));
    const defaultFinishes = (selectedJobType.default_finishes || []).filter(
      (finishId) => finishId && availableFinishIds.has(finishId),
    );

    autoWeedingApplyRef.current = true;
    prevJobTypeRef.current = jobType;

    console.warn("PieceCard: Clearing finishingOptions due to jobType change from", prevJobTypeRef.current, "to", jobType);

    updatePiece({
      finishes: Array.from(new Set(defaultFinishes)),
      finishingOptions: {},
      weedingComplexity: undefined,
    });
  }, [piece.jobType, jobTypes, availableFinishes, updatePiece]);

  const autoWeedingApplyRef = useRef(false);

  useEffect(() => {
    autoWeedingApplyRef.current = true;
  }, [piece.jobType]);

  // Handle Dreams job type: auto-select materials based on plain board flag
  useEffect(() => {
    if (piece.jobType === 'dreams') {
      const foamexMaterial = getMaterialsForJobType('dreams')?.find(
        (m) => m.id === '5mm-foamex'
      );

      const dreamsMaterials = [];

      // Always include foamex
      if (foamexMaterial) {
        dreamsMaterials.push(foamexMaterial);
      }

      // Include vinyl only if NOT plain board
      if (!piece.plainBoard) {
        const vinylMaterial = getMaterialsForJobType('dreams')?.find(
          (m) => m.id === 'standard-vinyl'
        );
        if (vinylMaterial) {
          dreamsMaterials.push(vinylMaterial);
        }
      }

      // Only update if materials aren't already set correctly
      const currentMaterialIds = (piece.materials || []).map((m) => m.id).sort().join(',');
      const dreamsMaterialIds = dreamsMaterials.map((m) => m.id).sort().join(',');

      if (currentMaterialIds !== dreamsMaterialIds) {
        updatePiece({ materials: dreamsMaterials });
      }
    }
  }, [piece.jobType, piece.plainBoard, piece.materials, getMaterialsForJobType, updatePiece]);

  // Handle jobs that require weeding complexity selection
  useEffect(() => {
    const jobType = piece.jobType;
    const requiresWeedingComplexity =
      jobType === 'cutvinyl' || jobType === 'vinyl-stickers';

    if (!requiresWeedingComplexity) {
      if (piece.weedingComplexity) {
        updatePiece({ weedingComplexity: undefined });
      }
      return;
    }

    const finishes = Array.isArray(piece.finishes) ? piece.finishes : [];
    const nextFinishes = new Set(finishes);
    let finishesChanged = false;

    if (jobType === 'cutvinyl') {
      const hasCutProcess = finishes.some(
        (id) => id === 'cutvinyl' || id === 'cut-vinyl-(no-cut-path)',
      );
      if (!hasCutProcess) {
        nextFinishes.add('cutvinyl');
        finishesChanged = true;
      }

      if (nextFinishes.delete('weeding')) {
        finishesChanged = true;
      }
      if (nextFinishes.delete('weeding-simple-stickers')) {
        finishesChanged = true;
      }

      if (autoWeedingApplyRef.current && !nextFinishes.has('weeding+application-tape')) {
        nextFinishes.add('weeding+application-tape');
        finishesChanged = true;
      }
    } else if (jobType === 'vinyl-stickers') {
      if (nextFinishes.delete('cutvinyl')) {
        finishesChanged = true;
      }
      if (nextFinishes.delete('cut-vinyl-(no-cut-path)')) {
        finishesChanged = true;
      }
      if (nextFinishes.delete('weeding+application-tape')) {
        finishesChanged = true;
      }

      if (autoWeedingApplyRef.current) {
        if (!nextFinishes.has('weeding-simple-stickers')) {
          nextFinishes.add('weeding-simple-stickers');
          finishesChanged = true;
        }
        if (!nextFinishes.has('kiss-cut')) {
          nextFinishes.add('kiss-cut');
          finishesChanged = true;
        }
        if (!nextFinishes.has('guillotining')) {
          nextFinishes.add('guillotining');
          finishesChanged = true;
        }
      }
    }

    const updates = {};
    if (finishesChanged) {
      updates.finishes = Array.from(nextFinishes);
    }

    if (!piece.weedingComplexity) {
      updates.weedingComplexity = 'easy';
    }

    if (Object.keys(updates).length > 0) {
      updatePiece(updates);
    }

    if (requiresWeedingComplexity && autoWeedingApplyRef.current) {
      autoWeedingApplyRef.current = false;
    }
  }, [piece.jobType, piece.finishes, piece.weedingComplexity, updatePiece]);

  useEffect(() => {
    if (!Array.isArray(piece.finishes) || !piece.finishes.length || !availableFinishes.length) {
      return;
    }

    const allowedFinishIds = new Set(availableFinishes.map((finish) => finish.id));
    const filteredFinishes = piece.finishes.filter((finishId) => allowedFinishIds.has(finishId));

    if (filteredFinishes.length !== piece.finishes.length) {
      updatePiece({ finishes: filteredFinishes });
    }
  }, [availableFinishes, piece.finishes, updatePiece]);

  useEffect(() => {
    if (!isRenaming) {
      setPendingName(piece.name || '');
    }
  }, [piece.name, isRenaming]);

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  console.log(`PieceCard Render ${pieceNumber}: jobType=${piece.jobType}, finishes=${JSON.stringify(piece.finishes)}, finishingOptions=${JSON.stringify(piece.finishingOptions)}, hasLaminateMats=${(finishingMaterials['laminate'] || []).length}`);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors ${isExpanded ? 'bg-blue-50' : 'bg-gray-50 hover:bg-gray-100'
          }`}
      >
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-medium text-gray-900">
            {piece.name && !isRenaming ? piece.name : `Item ${pieceNumber}`}
          </span>
          {isRenaming ? (
            <div
              className="flex items-center gap-2"
              onClick={(event) => event.stopPropagation()}
            >
              <input
                ref={renameInputRef}
                type="text"
                value={pendingName}
                onChange={(event) => setPendingName(event.target.value)}
                onKeyDown={handleRenameKeyDown}
                className="rounded border border-gray-300 px-2 py-1 text-xs shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Item name"
              />
              <button
                type="button"
                onClick={handleRenameSave}
                className="text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                Save
              </button>
              <button
                type="button"
                onClick={handleRenameCancel}
                className="text-xs text-gray-500 hover:text-gray-600"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleRenameStart}
              className="text-xs font-medium text-blue-600 hover:text-blue-700"
            >
              ✏️
            </button>
          )}
          {piece.jobType && (
            <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">
              {jobTypeName}
            </span>
          )}
          {!isExpanded && piece.standardSize && (
            <span className="text-xs text-gray-500">
              {piece.standardSize}
            </span>
          )}
          {!isExpanded && piece.width && piece.height && (
            <span className="text-sm text-gray-600">
              {piece.width}mm × {piece.height}mm
            </span>
          )}
          {!isExpanded && piece.quantity && (
            <span className="text-sm text-gray-600">Qty: {piece.quantity}</span>
          )}
          {!isExpanded && materialDisplay && (
            <span className="text-sm text-blue-600">{materialDisplay}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {allowRemove && (
            <button
              type="button"
              onClick={handleRemove}
              className="text-sm font-medium text-red-600 hover:text-red-700"
            >
              Remove
            </button>
          )}
          <span
            className={`text-lg font-semibold text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''
              }`}
          >
            ▶
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="space-y-4 border-t border-gray-200 bg-white px-4 py-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Job Type<span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              <select
                value={piece.jobType || ''}
                disabled={isLocked}
                onChange={(event) => {
                  const nextJobType = event.target.value;
                  updatePiece({
                    jobType: nextJobType,
                    width: '',
                    height: '',
                    standardSize: '',
                    quantity: 1,
                    materials: [],
                    finishes: [],
                    router_cut_length: undefined,
                    routerCutLength: undefined,
                    weeding_complexity: undefined,
                    machine_id: piece.machine_id,
                    plainBoard: false, // Reset plain board when changing job type
                  });
                }}
                className={`rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${piece.jobType === 'dreams' ? 'flex-1' : 'w-full'
                  } ${isLocked ? 'bg-gray-100 text-gray-500' : ''}`}
              >
                <option value="">Select job type…</option>
                {jobTypes.map((jobType) => (
                  <option key={jobType.id} value={jobType.id}>
                    {jobType.name}
                  </option>
                ))}
              </select>

              {piece.jobType === 'dreams' && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`plain-board-${piece.id || 'new'}`}
                    checked={piece.plainBoard || false}
                    disabled={isLocked}
                    onChange={(event) => updatePiece({ plainBoard: event.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                  />
                  <label
                    htmlFor={`plain-board-${piece.id || 'new'}`}
                    className={`cursor-pointer text-sm font-medium text-gray-700 ${isLocked ? 'cursor-not-allowed text-gray-400' : ''}`}
                  >
                    Plain Board
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className={`grid gap-4 ${isRollerBanner ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
            {!isRollerBanner && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Standard Size</label>
                <select
                  value={piece.standardSize || ''}
                  onChange={handleStandardSizeChange}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  {STANDARD_SIZES.map((size) => (
                    <option key={size.id || 'custom'} value={size.id}>
                      {size.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Width (mm)</label>
              <input
                type="number"
                min="0"
                value={piece.width ?? ''}
                onChange={(event) => handleDimensionChange('width', event.target.value)}
                disabled={isRollerBanner}
                className={`w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm ${isRollerBanner
                  ? 'cursor-not-allowed bg-gray-100 text-gray-500'
                  : 'focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                  }`}
                placeholder="e.g. 1000"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Height (mm)</label>
              <input
                type="number"
                min="0"
                value={piece.height ?? ''}
                onChange={(event) => handleDimensionChange('height', event.target.value)}
                disabled={isRollerBanner}
                className={`w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm ${isRollerBanner
                  ? 'cursor-not-allowed bg-gray-100 text-gray-500'
                  : 'focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                  }`}
                placeholder="e.g. 1500"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Quantity</label>
              <input
                type="number"
                min="1"
                max={maxQuantity}
                value={piece.quantity ?? 1}
                onChange={(event) => handleFieldChange('quantity', Number(event.target.value))}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Materials</label>
            {piece.jobType === 'dreams' ? (
              <div className="w-full rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-gray-700">
                {piece.plainBoard ? (
                  <>5mm Foamex (plain board @ £22.00/m²)</>
                ) : (
                  <>5mm Foamex with Standard Vinyl (pre-configured @ £27.30/m²)</>
                )}
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={toggleMaterialModal}
                  disabled={isLocked}
                  className={`w-full rounded border border-gray-300 px-3 py-2 text-sm text-left text-gray-700 shadow-sm transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${isLocked ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'hover:bg-gray-50'
                    }`}
                >
                  {materialDisplay || 'Select materials…'}
                </button>
                {piece.jobType && !availableMaterials.length && (
                  <p className="mt-1 text-xs text-amber-600">
                    No materials configured for this job type yet.
                  </p>
                )}
              </>
            )}
          </div>

          {piece.jobType && availableFinishes.length > 0 && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Finishes</label>
              <FinishSelector
                availableFinishes={availableFinishes}
                selectedFinishes={piece.finishes || []}
                onChange={(finishes) => onChange({ ...piece, finishes })}
                finishingMaterials={finishingMaterials}
                finishingOptions={piece.finishingOptions || {}}
                onFinishingOptionsChange={(finishingOptions) =>
                  onChange({ ...piece, finishingOptions })
                }
              />
            </div>
          )}

          {showWeedingDropdown && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Weeding Complexity</label>
              <select
                value={piece.weedingComplexity || 'easy'}
                onChange={(event) => updatePiece({ weedingComplexity: event.target.value })}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                {WEEDING_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {weedingHelpText}
              </p>
            </div>
          )}

          {requiresRouterLength && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Router Cut Length (mm)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  value={piece.router_cut_length ?? ''}
                  onChange={(event) => updatePiece({ router_cut_length: event.target.value })}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="Leave blank for automatic calculation"
                />
                <button
                  type="button"
                  className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => updatePiece({ router_cut_length: '' })}
                >
                  Auto
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Provide a custom total cut length for complex router jobs. Leave blank to auto-calculate.
              </p>
            </div>
          )}
        </div>
      )}

      {isMaterialModalOpen && (
        <MaterialSelectorModal
          availableMaterials={availableMaterials}
          selectedMaterials={piece.materials || []}
          onConfirm={handleMaterialsConfirm}
          onCancel={handleMaterialsCancel}
          allowMultiple={allowMultipleMaterials}
          groupBy={piece.jobType === 'cutvinyl' ? 'category' : 'type'}
          jobType={piece.jobType}
        />
      )}
    </div>
  );
};

export default PieceCard;
